import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  downloadWarrantyPdfApi,
  fetchWarrantyDetailsApi,
  updateWarrantyDetailsApi,
} from "../../api/warrantyDetailsApi";
import { WarrantyDetails } from "../../models/warrantlyDetails.model";

export interface WarrantyDetailsState {
  warrantyDetails: WarrantyDetails[];
  loading: boolean;
  error: string | null;
  downloadError: string | null;
  totalItems: number;
  totalRecords: number;
  totalPages: number;
  page: number;
  limit: number;
}

export const fetchWarrantyDetails = createAsyncThunk<
  {
    data: WarrantyDetails[];
    totalItems: number;
    totalRecords: number;
    totalPages: number;
  },
  {
    page?: number;
    limit?: number;
    search?: string;
    startDate?: string;
    endDate?: string;
    dateFilterType?: string;
    epcId?: string;
  },
  { rejectValue: string }
>(
  "warrantyDetails/fetchWarrantyDetails",
  async (
    {
      page = 1,
      limit = 10,
      search = "",
      startDate,
      endDate,
      dateFilterType,
      epcId,
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await fetchWarrantyDetailsApi({
        page,
        limit,
        search,
        startDate,
        endDate,
        dateFilterType,
        epcId,
      });
      return response;
    } catch (err: any) {
      return rejectWithValue(err.message || "Failed to fetch warranty details");
    }
  },
);

export const downloadWarrantyPdf = createAsyncThunk<
  { success: boolean; id: string }, // Return only serializable data (no Blob)
  { id: string; name: string }, // Accept object with id and name
  { rejectValue: string }
>("warranty/downloadWarrantyPdf", async ({ id, name }, { rejectWithValue }) => {
  try {
    const response = await downloadWarrantyPdfApi({ id });
    if (response.data instanceof Blob) {
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${name || "warranty"}_warranty_card.pdf`; // Use passed name for filename
      a.click();
      window.URL.revokeObjectURL(url);
      return { success: true, id }; // Return only serializable data
    }
    return rejectWithValue("Invalid PDF data");
  } catch (error: any) {
    return rejectWithValue(error?.message || "Failed to download warranty PDF");
  }
});

export const updateWarrantyDetails = createAsyncThunk<
  WarrantyDetails,
  { _id: string; data: Partial<WarrantyDetails> },
  { rejectValue: string }
>(
  "warrantyDetails/updateWarrantyDetails",
  async ({ _id, data }, { rejectWithValue }) => {
    try {
      const response = await updateWarrantyDetailsApi(_id, data);
      return response;
    } catch (err: any) {
      return rejectWithValue(
        err.message || "Failed to update warranty details",
      );
    }
  },
);

const initialState: WarrantyDetailsState = {
  warrantyDetails: [],
  loading: false,
  error: null,
  downloadError: null,
  totalItems: 0,
  totalRecords: 0,
  totalPages: 0,
  page: 1,
  limit: 10,
};

const warrantyDetailsSlice = createSlice({
  name: "warrantyDetails",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
      state.downloadError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWarrantyDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWarrantyDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.warrantyDetails = action.payload.data;
        state.totalItems = action.payload.totalItems;
        state.totalRecords = action.payload.totalRecords || 0;
        state.totalPages = action.payload.totalPages;
        state.page = action.meta.arg.page || 1;
        state.limit = action.meta.arg.limit || 10;
      })
      .addCase(fetchWarrantyDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch warranty details";
      })
      .addCase(updateWarrantyDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateWarrantyDetails.fulfilled, (state, action) => {
        state.loading = false;
        const updatedWarranty = action.payload;
        const index = state.warrantyDetails.findIndex(
          (item) => item._id === updatedWarranty._id,
        );
        if (index !== -1) {
          state.warrantyDetails[index] = updatedWarranty;
        }
      })
      .addCase(updateWarrantyDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to update warranty details";
      })
      .addCase(downloadWarrantyPdf.pending, (state) => {
        state.loading = true;
        state.downloadError = null;
      })
      .addCase(downloadWarrantyPdf.fulfilled, (state, action) => {
        state.loading = false;
        state.downloadError = null;
      })
      .addCase(downloadWarrantyPdf.rejected, (state, action) => {
        state.loading = false;
        state.downloadError = action.payload as string;
      });
  },
});

export const { clearError } = warrantyDetailsSlice.actions;
export default warrantyDetailsSlice.reducer;
